import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface TopbarSlotCtx {
  /** Elemento DOM do slot central do Topbar (alvo do portal), ou null. */
  el: HTMLElement | null;
  /** Ref callback que o Topbar passa pro seu <div> de slot. */
  setEl: (el: HTMLElement | null) => void;
  /** Largura (px) que o conteúdo do slot precisa — 0 = slot vazio. Hoje: o
   *  cabeçalho do paciente, que pede mais ou menos conforme o modo. */
  need: number;
  setNeed: (px: number) => void;
}

const Ctx = createContext<TopbarSlotCtx>({ el: null, setEl: () => {}, need: 0, setNeed: () => {} });

/**
 * Provider do slot central do Topbar.
 *
 * Permite que uma página (hoje, a ficha do paciente) injete conteúdo — o
 * cabeçalho do paciente — no espaço livre ao lado da busca global, via
 * `createPortal`, sem a página precisar viver dentro do Topbar. O Topbar e o
 * conteúdo roteado (Outlet) ficam distantes na árvore, então o portal é o
 * caminho limpo pra cruzar essa distância.
 */
export const TopbarSlotProvider = ({ children }: { children: ReactNode }) => {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [need, setNeed] = useState(0);
  return <Ctx.Provider value={{ el, setEl, need, setNeed }}>{children}</Ctx.Provider>;
};

/** Topbar: ref callback pro <div> que recebe o conteúdo portalizado. */
export const useTopbarSlotRef = () => useContext(Ctx).setEl;

/** Página: elemento-alvo pra `createPortal` (null enquanto o Topbar não montou). */
export const useTopbarSlotTarget = () => useContext(Ctx).el;

/** Topbar: quanto o conteúdo do slot precisa — a direita da barra cede até sobrar isso. */
export const useTopbarSlotNeed = () => useContext(Ctx).need;

/** Página: avisa quanto precisa do slot (0 = não está usando); solta ao desmontar. */
export const useClaimTopbarSlot = (needPx: number) => {
  const { setNeed } = useContext(Ctx);
  useEffect(() => {
    setNeed(needPx);
    return () => setNeed(0);
  }, [needPx, setNeed]);
};
